import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  all,
  fork,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  NOTIFICATIONCONTENTTYPE_BODY,
  NOTIFICATIONCONTENTTYPE_FOOTER,
  NOTIFICATIONCONTENTTYPE_HEADER,
  NOTIFICATIONCONTENTTYPE_SUBJECT,
  NOTIFICATIONCONTENTTYPE_TYPE,
} from '../../constants/api';
import { COMPONENT_REDUCER } from '../../constants/store';
import { downloadFileFromUrlWithApiKey } from '../../utilities/files';
import { removeAt, replaceAt } from '../../utilities/utilityFunctions';
import {
  sendNotificationError,
  sendNotificationInitialize,
  sendNotificationSuccess,
  updateNotificationContentInitialize,
} from '../actions/api';
import {
  notificationContentByIdSelector,
  notificationContentForNotificationContentSpecificationWithIdSelector,
} from '../apiFeatures/notificationContentSlice';
import { allNotificationContentTypesSelector } from '../apiFeatures/notificationContentTypeSlice';
import { baseApiUrlSelector } from '../bootstrapSlice';
import {
  globalMessageModes,
  parseApiError,
  setGlobalMessage,
} from '../globalMessageSlice';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { awaitApiCall, callAndAwaitApi } from '../helpers/sagaHelpers';

const initializeNotificationPanelState = createAction(
  'component/notificationPanel/initializeState',
);
const updateContentDiff = createAction(
  'component/notificationPanel/updateContentDiff',
);
const updateContentDiffInternal = createAction(
  'component/notificationPanel/updateContentDiff/internal',
);
const updateNotificationContentData = createAction(
  'component/notificationPanel/updateNotificationContentData',
);
const setIsUpdatingContent = createAction(
  'component/notificationPanel/setIsUpdatingContent',
);
const setIsNotificationPanelOpen = createAction(
  'component/notificationPanel/setIsPanelOpen',
);
const downloadTestNotification = createAction(
  'component/notificationPanel/downloadNotification',
);
const sendTestNotification = createAction(
  'component/notificationPanel/sendNotification',
);
const setIsCreatingTestNotification = createAction(
  'component/notificationPanel/setIsCreatingTestNotification',
);

const initialState = {
  contentDiffs: [],
  isPanelOpen: false,
  isUpdatingContent: false,
  isCreatingTestNotification: false,
};

// ---------- REDUCERS -----------
const notificationPanelReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(initializeNotificationPanelState, () => initialState)
    .addCase(updateContentDiffInternal, (state, action) => {
      state.contentDiffs = action.payload;
    })
    .addCase(setIsUpdatingContent, (state, action) => {
      state.isUpdatingContent = action.payload;
    })
    .addCase(setIsNotificationPanelOpen, (state, action) => {
      state.isPanelOpen = action.payload;
    })
    .addCase(setIsCreatingTestNotification, (state, action) => {
      state.isCreatingTestNotification = action.payload;
    })
    .addCase(sendNotificationSuccess, (state) => {
      state.isCreatingTestNotification = false;
    })
    .addCase(sendNotificationError, (state) => {
      state.isCreatingTestNotification = false;
    }),
);

// ---------- SAGAS -----------
export function* notificationPanelSaga() {
  yield all([
    fork(updateContentDiffSaga),
    fork(updateNotificationContentDataSaga),
    fork(downloadTestNotificationSaga),
    fork(sendTestNotificationSaga),
  ]);
}

function* downloadTestNotificationSaga() {
  yield takeLatest(downloadTestNotification.type, function* (action) {
    yield put(setIsCreatingTestNotification(true));
    const [baseApiUrl, hasChanges] = yield all([
      select(baseApiUrlSelector),
      select(hasChangesSelector),
    ]);
    const { hearingId, notificationType } = action.payload;

    var url = `${baseApiUrl}/hearing/${hearingId}/notification/${notificationType}/export`;
    if (hasChanges) {
      yield put(
        setGlobalMessage({
          mode: globalMessageModes.downloadNotificationHasChanges,
        }),
      );
    }

    yield downloadFileFromUrlWithApiKey(url);
    yield put(setIsCreatingTestNotification(false));
  });
}

function* sendTestNotificationSaga() {
  yield takeLatest(sendTestNotification.type, function* (action) {
    yield put(setIsCreatingTestNotification(true));
    const actionResult = yield callAndAwaitApi(
      sendNotificationInitialize(action.payload),
    );

    if (actionResult.success) {
      const hasChanges = yield select(hasChangesSelector);
      yield put(
        setGlobalMessage({
          mode: hasChanges
            ? globalMessageModes.sendNotificationHasChanges
            : globalMessageModes.sendNotificationSuccessful,
        }),
      );
    } else {
      yield put(
        setGlobalMessage({
          mode: globalMessageModes.sendNotificationFailed,
        }),
      );
    }
  });
}

function* updateNotificationContentDataSaga() {
  yield takeLeading(updateNotificationContentData.type, function* (action) {
    const { hearingId, notificationContentSpecificationId } = action.payload;
    yield put(setIsUpdatingContent(true));

    const hasChanges = yield select(hasChangesSelector);
    if (!hasChanges) {
      yield put(initializeNotificationPanelState());
      return;
    }

    const [contentDiffs, notificationContentsFromState] = yield all([
      select(contentDiffsSelector),
      select(
        notificationContentForNotificationContentSpecificationWithIdSelector(
          notificationContentSpecificationId,
        ),
      ),
    ]);

    // Creates a deep copy of notificationContents to avoid modifying a readonly object directly from redux-store
    const notificationContents = JSON.parse(
      JSON.stringify(notificationContentsFromState),
    );

    const contentWithChanges = [];
    for (let diff of contentDiffs) {
      const currentNotificationContent = notificationContents.find(
        (nc) => getEntityId(nc) === diff.contentId,
      );
      const newTextContent = replaceLineEndings(diff.textContent);

      if (
        newTextContent !== getAttr(currentNotificationContent, 'textContent')
      ) {
        contentWithChanges.push({
          ...currentNotificationContent,
          attributes: {
            ...(currentNotificationContent.attributes ?? {}),
            textContent: newTextContent,
          },
        });
      }
    }

    const dtos = contentWithChanges.map((content) => ({
      data: content,
    }));

    const initAction = yield put(
      updateNotificationContentInitialize({
        hearingId,
        notificationContentSpecificationId,
        dtos,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);
    if (actionResult.error) {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(updateContentDiffInternal([]));
    yield put(setIsUpdatingContent(false));
  });
}

function* updateContentDiffSaga() {
  yield takeLatest(updateContentDiff.type, function* (action) {
    const newContent = action.payload;
    const id = newContent.contentId;

    const [diffs, notificationContentFromState] = yield all([
      select(contentDiffsSelector),
      select(notificationContentByIdSelector(id)),
    ]);

    const prevText = getAttr(notificationContentFromState, 'textContent');
    const newText = newContent.textContent;

    const indexOfContent = diffs.findIndex(
      (content) => content.contentId === id,
    );

    let newDiff = diffs;

    if (newText === prevText && indexOfContent !== -1) {
      newDiff = removeAt(diffs, indexOfContent);
    } else {
      newDiff =
        indexOfContent === -1
          ? [...diffs, newContent]
          : replaceAt(diffs, indexOfContent, newContent);
    }

    if (newDiff !== diffs) {
      yield put(updateContentDiffInternal(newDiff));
    }
  });
}

// ---------- SELECTORS -----------
const notificationPanelState = (state) =>
  state.component[COMPONENT_REDUCER.NOTIFICATION_PANEL];

const isNotificationPanelOpenSelector = createSelector(
  notificationPanelState,
  (state) => state.isPanelOpen,
);

const isUpdatingContentSelector = createSelector(
  notificationPanelState,
  (state) => state.isUpdatingContent,
);

const isCreatingTestNotificationSelector = createSelector(
  notificationPanelState,
  (state) => state.isCreatingTestNotification,
);

const contentDiffsSelector = createSelector(
  notificationPanelState,
  (state) => state.contentDiffs,
);

const hasChangesSelector = createSelector(
  contentDiffsSelector,
  (contentDiffs) => Array.isArray(contentDiffs) && contentDiffs.length > 0,
);

const notificationContentDataSelector = (notificationContents) =>
  createSelector(
    allNotificationContentTypesSelector,
    (notificationContentTypes) =>
      mapNotificationContentAndTypeToNotificationContentData(
        notificationContents,
        notificationContentTypes,
      ),
  );

const notificationContentDataWithDiffSelector = (notificationContents) =>
  createSelector(
    [
      notificationContentDataSelector(notificationContents),
      contentDiffsSelector,
    ],
    (notificationContentData, contentDiffs) => {
      // Creates a deep copy of notificationContentData to avoid modifying a readonly object directly from redux-store
      let notificationContentDataWithDiff = JSON.parse(
        JSON.stringify(notificationContentData),
      );

      notificationContentDataWithDiff = notificationContentDataWithDiff.map(
        replaceNewLine,
      );

      contentDiffs.forEach((diff) => {
        const indexOfContent = notificationContentDataWithDiff.findIndex(
          (content) => content.contentId === diff.contentId,
        );

        if (indexOfContent === -1) {
          return;
        }
        notificationContentDataWithDiff[indexOfContent].textContent =
          diff.textContent;
      });

      return notificationContentDataWithDiff;
    },
  );

export {
  downloadTestNotification,
  sendTestNotification,
  hasChangesSelector,
  initializeNotificationPanelState,
  isNotificationPanelOpenSelector,
  isUpdatingContentSelector,
  isCreatingTestNotificationSelector,
  notificationContentDataWithDiffSelector,
  setIsNotificationPanelOpen,
  updateContentDiff,
  updateNotificationContentData,
};
export default notificationPanelReducer;

const NotificationContentTypeOrder = [
  NOTIFICATIONCONTENTTYPE_SUBJECT,
  NOTIFICATIONCONTENTTYPE_HEADER,
  NOTIFICATIONCONTENTTYPE_BODY,
  NOTIFICATIONCONTENTTYPE_FOOTER,
];

const orderNotificaionContentsByNotificationContentType = (
  notificationContentTypes,
  typeOrder,
) => {
  const rank = new Map(typeOrder.map((value, idx) => [value, idx]));

  return (a, b) => {
    const aContentType = notificationContentTypes.find(
      (nct) => getEntityId(nct) === getRelId(a, NOTIFICATIONCONTENTTYPE_TYPE),
    );
    const bContentType = notificationContentTypes.find(
      (nct) => getEntityId(nct) === getRelId(b, NOTIFICATIONCONTENTTYPE_TYPE),
    );
    return (
      (rank.get(getAttr(aContentType, 'notificationContentType')) ??
        Number.POSITIVE_INFINITY) -
      (rank.get(getAttr(bContentType, 'notificationContentType')) ??
        Number.POSITIVE_INFINITY)
    );
  };
};

const mapNotificationContentAndTypeToNotificationContentData = (
  notificationContents,
  notificationContentTypes,
) => {
  const sortedNotificationContents = notificationContents.sort(
    orderNotificaionContentsByNotificationContentType(
      notificationContentTypes,
      NotificationContentTypeOrder,
    ),
  );
  return sortedNotificationContents.map((nc) => {
    const notificationContentType = notificationContentTypes.find(
      (nct) => getEntityId(nct) === getRelId(nc, NOTIFICATIONCONTENTTYPE_TYPE),
    );
    return {
      type: getAttr(notificationContentType, 'notificationContentType'),
      canEdit: getAttr(notificationContentType, 'canEdit'),
      contentId: getEntityId(nc),
      textContent: getAttr(nc, 'textContent'),
    };
  });
};

const replaceNewLine = (content) => {
  return {
    ...content,
    textContent: content.textContent.replace(/\{\{NewLine\}\}/g, '\n'),
  };
};

// Replaces line endings with NewLine, to match what is expected in the backend
// 'A Text \n\nWith line endings\n' -> 'A Text{{NewLine}}{{NewLine}}With line endings'
const replaceLineEndings = (content) =>
  String(content ?? '')
    .replace(/[^\S\r\n]*(?:\r\n|\r|\n)/g, '{{NewLine}}')
    .replace(/(?:\s*(?:\{\{\s*NewLine\s*\}\}))+$/i, '');
